'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

type Locale = 'fr' | 'en' | 'es';

interface MarketingHeaderProps {
  locale: Locale;
  onLocaleChange?: (locale: Locale) => void;
}

// ─── PICTOGRAM — Supabase-bolt style, thick & filled ─────────────────────────
function LoyzenPicto({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none" aria-hidden="true">
      {/* Outer orbit ring — thick */}
      <circle cx="18" cy="18" r="15" stroke="#3ecf8e" strokeWidth="1.8" strokeDasharray="4 2.5" opacity="0.4" />
      {/* Connection lines — thick, bold */}
      <line x1="18" y1="12.5" x2="18" y2="5" stroke="#3ecf8e" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <line x1="21" y1="20" x2="28" y2="29" stroke="#3ecf8e" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      <line x1="15" y1="20" x2="8" y2="29" stroke="#3ecf8e" strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
      {/* Satellite nodes — large, fully filled */}
      <circle cx="18" cy="3.5" r="3.5" fill="#3ecf8e" />
      <circle cx="30" cy="30.5" r="3.5" fill="#3ecf8e" />
      <circle cx="6" cy="30.5" r="3.5" fill="#3ecf8e" />
      {/* Hub — large, bold */}
      <circle cx="18" cy="18" r="6" fill="#3ecf8e" />
      {/* Inner highlight */}
      <circle cx="18" cy="18" r="2.8" fill="#d1fae5" opacity="0.95" />
    </svg>
  );
}

// ─── Dropdown item pictograms ─────────────────────────────────────────────────
const dropdownPictos: Record<string, JSX.Element> = {
  // How it works / Fonctionnement / Cómo funciona
  'how-it-works': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="9" height="14" rx="2"/>
      <rect x="13" y="3" width="9" height="14" rx="2"/>
      <path d="M7 17v2M17 17v2M4 21h6M14 21h6"/>
    </svg>
  ),
  // Features / Fonctionnalités / Funcionalidades
  'features': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
    </svg>
  ),
  // POS & Terminal / Caisse & Terminal / Caja & Terminal
  'pos-terminal': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="13" height="10" rx="2"/>
      <rect x="9" y="11" width="6" height="10" rx="2"/>
      <path d="M5 13v2M5 17v2"/>
    </svg>
  ),
  // Rewards / Récompenses / Recompensas
  'rewards': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2"/>
      <rect x="2" y="7" width="20" height="5" rx="1.5"/>
      <path d="M12 7V21"/>
      <path d="M12 7c0 0-2.5-3.5-4.5-2S8.5 9 12 7z"/>
      <path d="M12 7c0 0 2.5-3.5 4.5-2S15.5 9 12 7z"/>
    </svg>
  ),
  // Team & Security / Équipe & Sécurité / Equipo & Seguridad
  'team': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.5"/>
      <path d="M3 20c0-3.31 2.69-6 6-6s6 2.69 6 6"/>
      <circle cx="18" cy="9" r="2.5" opacity="0.7"/>
      <path d="M21 20c0-2.76-1.79-5.12-4.5-5.73" opacity="0.7"/>
    </svg>
  ),
  // Analytics / Pilotage / Control
  'analytics': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="14" width="4" height="7" rx="1"/>
      <rect x="10" y="9" width="4" height="12" rx="1"/>
      <rect x="17" y="4" width="4" height="17" rx="1"/>
      <path d="M5 14L12 9L19 4" strokeDasharray="2 2" opacity="0.5"/>
    </svg>
  ),
  // Pricing / Tarifs / Precios
  'pricing': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M12 7v10M9.5 9.5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5c0 1.38-1.12 2.5-2.5 2.5s-2.5 1.12-2.5 2.5 1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5"/>
    </svg>
  ),
  // FAQ
  'faq': (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <path d="M9.5 9.5a2.5 2.5 0 015 0c0 2-2.5 2.5-2.5 4.5"/>
      <circle cx="12" cy="17.5" r="0.8" fill="currentColor"/>
    </svg>
  ),
};

// ─── Map href to picto key ────────────────────────────────────────────────────
function getPictoKey(href: string): string {
  if (href.includes('how-it-works')) return 'how-it-works';
  if (href.includes('features#') && href.includes('caisse')) return 'pos-terminal';
  if (href.includes('features#') && href.includes('pos')) return 'pos-terminal';
  if (href.includes('features#') && href.includes('caja')) return 'pos-terminal';
  if (href.includes('features#') && (href.includes('recompens') || href.includes('reward'))) return 'rewards';
  if (href.includes('features#') && (href.includes('equipe') || href.includes('team') || href.includes('equipo'))) return 'team';
  if (href.includes('features#') && (href.includes('pilotage') || href.includes('analytics') || href.includes('control'))) return 'analytics';
  if (href === '/features') return 'features';
  if (href.includes('pricing')) return 'pricing';
  if (href.includes('faq')) return 'faq';
  return 'features';
}

// ─── NAV CONTENT — links to existing pages ───────────────────────────────────
const navLinks: Record<Locale, { label: string; href: string; dropdown?: { label: string; href: string; desc: string }[] }[]> = {
  fr: [
    {
      label: 'Produit',
      href: '/how-it-works',
      dropdown: [
        { label: 'Fonctionnement', href: '/how-it-works', desc: 'Double écran terminal + caisse' },
        { label: 'Fonctionnalités', href: '/features', desc: 'Toutes les fonctionnalités' },
        { label: 'Caisse & Terminal', href: '/features#caisse', desc: 'Synchronisation vendeur / client' },
        { label: 'Récompenses', href: '/features#recompenses', desc: '4 types de récompenses' },
        { label: 'Équipe & Sécurité', href: '/features#equipe', desc: 'PIN, rôles, terminaux' },
        { label: 'Pilotage', href: '/features#pilotage', desc: 'Tableau de bord en temps réel' },
      ],
    },
    { label: 'Tarifs', href: '/pricing' },
    { label: 'FAQ', href: '/#faq' },
  ],
  en: [
    {
      label: 'Product',
      href: '/how-it-works',
      dropdown: [
        { label: 'How it works', href: '/how-it-works', desc: 'Dual screen terminal + POS' },
        { label: 'Features', href: '/features', desc: 'All features' },
        { label: 'POS & Terminal', href: '/features#pos', desc: 'Vendor / customer sync' },
        { label: 'Rewards', href: '/features#rewards', desc: '4 reward types' },
        { label: 'Team & Security', href: '/features#team', desc: 'PIN, roles, terminals' },
        { label: 'Analytics', href: '/features#analytics', desc: 'Real-time dashboard' },
      ],
    },
    { label: 'Pricing', href: '/pricing' },
    { label: 'FAQ', href: '/#faq' },
  ],
  es: [
    {
      label: 'Producto',
      href: '/how-it-works',
      dropdown: [
        { label: 'Cómo funciona', href: '/how-it-works', desc: 'Doble pantalla terminal + caja' },
        { label: 'Funcionalidades', href: '/features', desc: 'Todas las funcionalidades' },
        { label: 'Caja & Terminal', href: '/features#caja', desc: 'Sincronización vendedor / cliente' },
        { label: 'Recompensas', href: '/features#recompensas', desc: '4 tipos de recompensas' },
        { label: 'Equipo & Seguridad', href: '/features#equipo', desc: 'PIN, roles, terminales' },
        { label: 'Control', href: '/features#control', desc: 'Panel en tiempo real' },
      ],
    },
    { label: 'Precios', href: '/pricing' },
    { label: 'FAQ', href: '/#faq' },
  ],
};

const ctaLabels: Record<Locale, { signin: string; signup: string }> = {
  fr: { signin: 'Se connecter', signup: 'Essayer gratuitement' },
  en: { signin: 'Sign in', signup: 'Try for free' },
  es: { signin: 'Iniciar sesión', signup: 'Probar gratis' },
};

export default function MarketingHeader({ locale, onLocaleChange }: MarketingHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const links = navLinks[locale];
  const cta = ctaLabels[locale];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-2xl border-b' : 'bg-transparent'
      }`}
      style={scrolled ? { background: 'rgba(10,10,10,0.95)', borderColor: 'rgba(255,255,255,0.07)' } : {}}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-16">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'h-[52px]' : 'h-[68px]'}`}>

          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <div className="relative">
              <LoyzenPicto size={scrolled ? 24 : 28} />
              <div className="absolute inset-0 blur-xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 bg-[#3ecf8e] rounded-full" />
            </div>
            <span
              className="transition-all duration-300 text-white select-none"
              style={{
                fontFamily: '"DM Sans", "Plus Jakarta Sans", system-ui, sans-serif',
                fontWeight: 800,
                fontSize: scrolled ? '19px' : '21px',
                letterSpacing: '-0.035em',
                lineHeight: 1,
              }}
            >
              loyzen
            </span>
          </Link>

          {/* Desktop nav */}
          <nav ref={dropdownRef} className="hidden md:flex items-center gap-0">
            {links.map((link) => (
              <div key={link.href + link.label} className="relative">
                {link.dropdown ? (
                  <button
                    onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                    className="relative flex items-center gap-1 px-4 py-2 text-[13.5px] font-normal transition-colors duration-200 group"
                    style={{ color: openDropdown === link.label ? 'white' : '#a1a1aa' }}
                  >
                    {link.label}
                    <svg
                      width="12" height="12" viewBox="0 0 12 12" fill="none"
                      className={`transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`}
                    >
                      <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ) : (
                  <Link
                    href={link.href}
                    className="relative px-4 py-2 text-[13.5px] font-normal transition-colors duration-200 block hover:text-white"
                    style={{ color: '#a1a1aa' }}
                  >
                    {link.label}
                  </Link>
                )}

                {/* Mega dropdown */}
                {link.dropdown && openDropdown === link.label && (
                  <div
                    className="absolute top-full left-0 mt-2 rounded-2xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.8)]"
                    style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.09)', width: 320 }}
                  >
                    {/* Dropdown header */}
                    <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ fontSize: 10, color: '#52525b', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        {link.label}
                      </span>
                    </div>
                    <div className="p-2">
                      {link.dropdown.map((item) => {
                        const pictoKey = getPictoKey(item.href);
                        const picto = dropdownPictos[pictoKey];
                        return (
                          <Link
                            key={item.href + item.label}
                            href={item.href}
                            onClick={() => setOpenDropdown(null)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group"
                            style={{ color: 'inherit', textDecoration: 'none' }}
                            onMouseEnter={e => {
                              (e.currentTarget.style.background = 'rgba(255,255,255,0.05)');
                              const icon = e.currentTarget.querySelector('.menu-icon') as HTMLElement;
                              if (icon) icon.style.color = '#70F59A';
                            }}
                            onMouseLeave={e => {
                              (e.currentTarget.style.background = 'transparent');
                              const icon = e.currentTarget.querySelector('.menu-icon') as HTMLElement;
                              if (icon) icon.style.color = '#52525b';
                            }}
                          >
                            {/* Picto container */}
                            <div
                              className="menu-icon flex-shrink-0 transition-colors duration-150"
                              style={{ color: '#52525b', width: 18, height: 18 }}
                            >
                              {picto}
                            </div>
                            {/* Text */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="text-[13px] font-medium" style={{ color: '#e2e8f0' }}>{item.label}</div>
                              <div className="text-[11px] mt-0.5" style={{ color: '#52525b' }}>{item.desc}</div>
                            </div>
                            {/* Arrow */}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3a3a3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                              <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                            </svg>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              href="/login"
              className="px-4 py-[7px] text-[13.5px] font-normal transition-colors duration-200 hover:text-white"
              style={{ color: '#a1a1aa' }}
            >
              {cta.signin}
            </Link>
            <Link
              href="/signup"
              className="group relative px-4 py-[7px] rounded-lg text-[13.5px] font-semibold overflow-hidden transition-all duration-200 hover:opacity-90"
              style={{ background: '#3ecf8e', color: '#0a1a0e', boxShadow: '0 0 0 1px rgba(62,207,142,0.3)' }}
            >
              <span className="relative z-10">{cta.signup}</span>
            </Link>
          </div>

          {/* Mobile menu button — with picto */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex md:hidden items-center gap-2 p-2 rounded-lg transition-all duration-150 hover:text-white"
            style={{ color: '#a1a1aa' }}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M3.293 3.293a1 1 0 011.414 0L9 7.586l4.293-4.293a1 1 0 111.414 1.414L10.414 9l4.293 4.293a1 1 0 01-1.414 1.414L9 10.414l-4.293 4.293a1 1 0 01-1.414-1.414L7.586 9 3.293 4.707a1 1 0 010-1.414z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
                <path fillRule="evenodd" clipRule="evenodd" d="M1 4a1 1 0 011-1h14a1 1 0 110 2H2a1 1 0 01-1-1zm0 5a1 1 0 011-1h14a1 1 0 110 2H2a1 1 0 01-1-1zm0 5a1 1 0 011-1h14a1 1 0 110 2H2a1 1 0 01-1-1z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden backdrop-blur-xl border-t" style={{ background: 'rgba(10,10,10,0.98)', borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="px-5 py-4 space-y-0.5">
            {links.map((link) => (
              <div key={link.href + link.label + 'mob'}>
                <Link
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] font-light transition-colors hover:text-white"
                  style={{ color: '#a1a1aa' }}
                >
                  <span style={{ color: '#52525b', width: 18, height: 18, display: 'flex', alignItems: 'center' }}>
                    {dropdownPictos[getPictoKey(link.href)] ?? dropdownPictos['features']}
                  </span>
                  {link.label}
                </Link>
                {link.dropdown && (
                  <div className="ml-4 pl-3 border-l space-y-0.5 mt-0.5 mb-1" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.href + item.label + 'sub'}
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-[13px] transition-colors hover:text-white"
                        style={{ color: '#71717a' }}
                      >
                        <span style={{ color: '#3f3f46', width: 16, height: 16, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {dropdownPictos[getPictoKey(item.href)]}
                        </span>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 pb-1 flex flex-col gap-2">
              <Link
                href="/login"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-xl text-[14px] text-center transition-colors hover:text-white"
                style={{ color: '#a1a1aa', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {cta.signin}
              </Link>
              <Link
                href="/signup"
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 rounded-xl text-[14px] font-semibold text-center"
                style={{ background: '#3ecf8e', color: '#0a1a0e' }}
              >
                {cta.signup}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
